import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { formatApiError } from 'shared/errorUtils';

/**
 * WeekCopyWizardModal
 * - mode default: PACKAGE_ONLY
 * - skipTelafi: true (UI yok)
 */
export function WeekCopyWizardModal({
  open,
  onClose,
  onPreview, // async (payload) => previewResult
  defaultFromWeekStartISO,
  defaultToWeekStartISO,
  rangeDays = 7, // ✅ opsiyonel
  trainerId = null,
}) {
  const [fromWeekStartISO, setFromWeekStartISO] = React.useState(defaultFromWeekStartISO || '');
  const [toWeekStartISO, setToWeekStartISO] = React.useState(defaultToWeekStartISO || '');
  const [scope, setScope] = React.useState('PACKAGE_ONLY');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setErr('');
    setBusy(false);
    setFromWeekStartISO(defaultFromWeekStartISO || '');
    setToWeekStartISO(defaultToWeekStartISO || '');
    setScope('PACKAGE_ONLY');
  }, [open, defaultFromWeekStartISO, defaultToWeekStartISO]);

  const sameWeek =
    Boolean(fromWeekStartISO) && Boolean(toWeekStartISO) && fromWeekStartISO === toWeekStartISO;

  const canPreview = Boolean(fromWeekStartISO) && Boolean(toWeekStartISO) && !busy && !sameWeek;

  const handlePreview = async () => {
    if (!canPreview) return;
    setErr('');
    setBusy(true);
    try {
      const payload = {
        fromWeekStartISO,
        toWeekStartISO,
        mode: scope,
        skipTelafi: true,

        rangeDays,
      };

      await onPreview?.(payload);
    } catch (e) {
      setErr(formatApiError(e, 'Onizleme olusturulamadi.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1.2 }}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon>content_copy</Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Hafta Kopyala
          </MDTypography>
        </MDBox>
        <MDTypography variant="caption" sx={{ opacity: 0.8 }}>
          Telafiler kopyalanmaz. Hedef haftada dolu saatler atlanır. Yoklamalar sıfırlanır.
        </MDTypography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.4 }}>
          <TextField
            label="Hangi haftadan? (Pazartesi)"
            type="date"
            value={fromWeekStartISO}
            onChange={(e) => setFromWeekStartISO(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={busy}
            fullWidth
          />

          <TextField
            label="Hangi haftaya? (Pazartesi)"
            type="date"
            value={toWeekStartISO}
            onChange={(e) => setToWeekStartISO(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={busy}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="scope-label">Kapsam</InputLabel>
            <Select
              labelId="scope-label"
              label="Kapsam"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              disabled={busy}
            >
              <MenuItem value="PACKAGE_ONLY">Sadece paket seansları</MenuItem>
              <MenuItem value="ALL">Tüm seanslar</MenuItem>
            </Select>
          </FormControl>

          {sameWeek ? (
            <MDBox
              sx={{
                p: 1.2,
                borderRadius: 2,
                border: '1px solid rgba(239,68,68,.35)',
                background: 'rgba(239,68,68,.08)',
              }}
            >
              <MDTypography variant="caption" fontWeight="bold">
                Aynı haftaya kopyalama yapılamaz.
              </MDTypography>
            </MDBox>
          ) : null}

          {err ? (
            <MDBox
              sx={{
                p: 1.2,
                borderRadius: 2,
                border: '1px solid rgba(239,68,68,.35)',
                background: 'rgba(239,68,68,.08)',
              }}
            >
              <MDTypography variant="caption" fontWeight="bold" sx={{ whiteSpace: 'pre-line' }}>
                {err}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 2.2, py: 1.6 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={busy}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 800 }}
        >
          Vazgeç
        </MDButton>

        <MDButton
          variant="contained"
          color="info"
          onClick={handlePreview}
          disabled={!canPreview}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
        >
          {busy ? 'Önizleme...' : 'Önizleme Oluştur'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
